import { BlueprintInstanceType } from '../shared.enums';
import { getFuelSkuSuffix } from '../../helpers';
import { itemData } from './item.data';
import { Publish } from '../../decorators';
import { Size } from '../../types';

import { Component, ComponentCategory, ComponentVendor, DefaultPlacement, SkuOverheadMode } from '../../component-classes';
import { ItemData, ItemDataIn, ItemDataOut } from './types';
import { KitchenDesignBase, KitchenDesignOptions } from '../kitchen-design-base';


export type ItemConstructorParams = KitchenDesignOptions<ItemDataIn> & {
	sku?: string;
	id?: string;
};

export class Item extends KitchenDesignBase<ItemDataIn> {
	#component: Component;
	#override: boolean;
	#fuelchoice: string | null;
	#skuColor: string | null;
	#displayText: string;

	public exclude: boolean;

	constructor (options:ItemConstructorParams = {}) {
		super(BlueprintInstanceType.Item, options);

		const { dataIn = null, sku = null, id = null } = options;
		const data = itemData(dataIn);

		if (data.override) {
			this.#component = new Component({ userEmail: null, dataIn: data.component });
			this._id = data.id;
			this.#override = data.override;
			this.component.readonly = false;
		} else {
			this.#override = false;

			if (sku) {
				// new item
				this.#component = this.context.components.findSku(sku);
				this._id = this.component?.id ?? sku;
			} else if (id) {
				this.#component = this.context.components.findId(id);
				this._id = id;
			} else if (dataIn?.id) {
				this.#component = this.context.components.findId(dataIn.id);
				this._id = dataIn.id;
			}

			// A fuel appliance is one catalog row per fuel, so bind the row for this item's own
			// fuelchoice when it has one, else the kitchen's. Makes the picker, a saved design
			// stored against any variant (or a pre-split base id) and a fuel switch all agree.
			const fuelVariant = this.context.components.resolveFuelVariant(this.component, data.fuelchoice ?? this.refMap.kitchen?.fuelchoice ?? null);
			if (fuelVariant && fuelVariant !== this.component) {
				this.#component = fuelVariant;
				this._id = fuelVariant.id;
			}

			// Catalog miss (component data not loaded yet, or a discontinued sku): fall back to a
			// zero-size placeholder so one unresolved item can't crash the entire design build. Not
			// serialized (non-override items write `component: null`), so the real component resolves
			// on a later load once the catalog is present.
			if (!this.component) {
				console.warn(`Item ${this._id ?? sku ?? dataIn?.id}: component not found in catalog — using placeholder`);
				this.#component = new Component({ userEmail: null, dataIn: { id: this._id ?? undefined, sku: '', size: { width: 0, height: 0, depth: 0 }, cutout: { width: 0, height: 0, depth: 0 } } });
			}
		}
		this.#fuelchoice = data.fuelchoice ?? null;
		this.#skuColor = data.skuColor ?? null;
		this.#subscribeComponent();

		this.#displayText = this.#computeDisplayText();

		this.setInitialized();
	}


	/**
	 * Computes which text to display: customLabel if set, then displayName, then sku.
	 * The 'CUTOUT' sentinel sku is an internal marker, not a user-facing name, so an
	 * unnamed custom cutout displays blank rather than falling back to it.
	 */
	#computeDisplayText (): string {
		const sku = this.component?.sku === 'CUTOUT' ? '' : this.component?.sku;
		return this.component?.customLabel || this.component?.displayName || sku || '';
	}

	/** Forwards the bound component's events and keeps the cached display text in step with it */
	#subscribeComponent (): void {
		if (!this.#component) return;

		this.#component.subscribe('*', '*', (event) => this.publishEvent(event));

		// Recompute display text when customLabel, sku, or label changes on the component
		this.#component.subscribe('component_custom_label', 'updated', () => { this.#displayText = this.#computeDisplayText(); });
		this.#component.subscribe('component_sku', 'updated', () => { this.#displayText = this.#computeDisplayText(); });
	}

	/**
	 * Repoints a catalog-backed item at its row in whatever catalog `context.components` now holds.
	 * `applyPriceBook` swaps that collection wholesale, and the instance bound at construction would
	 * otherwise keep reporting the retired snapshot's msrp while islands and countertops — which
	 * resolve per read — report the new one. An override item is a private snapshot, not a catalog
	 * row, so it is left alone.
	 * @returns true when a different component instance was bound
	 */
	public rebindComponent (): boolean {
		if (this.#override) return false;

		const rebound = this.context.components.findId(this._id);
		if (!rebound || rebound === this.#component) return false;

		this.#component = rebound;
		this.#subscribeComponent();
		this.#displayText = this.#computeDisplayText();

		return true;
	}

	get component (): Component { return this.#component }

	// shortcut getters
	get category (): ComponentCategory { return this.component?.category ?? null }
	get description (): string { return this.component?.description ?? null }
	get blueprintDescription (): string { return this.component?.blueprintDescription ?? null }
	get distanceAboveCountertop (): number { return this.component?.distanceAboveCountertop ?? null }
	get distanceBelowCountertop (): number { return this.component?.distanceBelowCountertop ?? null }
	get distanceFrontOfFace (): number { return this.component?.distanceFrontOfFace ?? null }
	get distanceBackOfFace (): number { return this.component?.distanceBackOfFace ?? null }
	get defaultPlacement (): DefaultPlacement { return this.component?.defaultPlacement ?? null }
	get fuel (): boolean { return this.component?.fuel ?? false }
	get showFuelSku (): boolean { return this.component?.showFuelSku ?? true }
	get placement (): string { return this.component?.placement ?? null }
	get vendor (): ComponentVendor { return this.component?.vendor ?? null }

	@Publish('item_msrp', 'updated')
	set msrp (msrp: number) {
		if (!this.#override) return;
		this.component.msrp = msrp;
	}
	get msrp (): number { return this.component?.msrp ?? 0 }

	@Publish('item_cutout', 'updated')
	set cutout (cutout: Partial<Size>) {
		if (!this.#override) return;
		this.component.cutout = { ...this.component.cutout, ...cutout };
	}
	get cutout (): Size { return this.component?.cutout ?? null }

	// A component that is fuel:true purely to keep its rule-managed vents (pellet grills, #237)
	// but has showFuelSku:false doesn't actually run on LP/NG -- it has no real fuel choice to
	// store or report, same as a non-fuel item (#392/#458).
	@Publish('item_fuealchoice', 'updated')
	set fuelchoice (fuelchoice: string | null) {
		if (this.component?.fuel && this.component?.showFuelSku) this.#fuelchoice = fuelchoice;
	}
	get fuelchoice (): string | null { return (this.component?.fuel && this.component?.showFuelSku) ? this.#fuelchoice : null }

	@Publish('item_sku', 'updated')
	set sku (sku: string) {
		if (!this.#override) return;
		this.component.sku = sku;
	}
	get sku (): string { return this.component?.sku ?? '' }
	get label (): string { return this.component?.label ?? '' }
	get customLabel (): string { return this.component?.customLabel ?? '' }
	get hsProductId (): string | null { return this.component?.hsProductId ?? null }

	/** Whether to show any label/sku text at all */
	get showLabel (): boolean { return this.component?.showLabel ?? true }

	/** Pre-computed display name from component data */
	get displayName (): string { return this.component?.displayName ?? '' }

	/** Whether this item shows in overhead/overhead-manufacturing views */
	get showInOverhead (): boolean { return this.component?.showInOverhead ?? true }

	/** Whether this item shows in sku-overhead/sku-overhead-manufacturing views */
	get showInSkuOverhead (): boolean { return this.component?.showInSkuOverhead ?? true }

	/** When showInSkuOverhead is true, controls whether the visual ('image') or the SKU badge ('sku') is rendered */
	get skuOverheadMode (): SkuOverheadMode { return this.component?.skuOverheadMode ?? 'sku' }

	/** The label color preset name from component data */
	get skuColorPreset (): string { return this.component?.skuColorPreset ?? 'White' }

	/** Pre-computed display text: customLabel if set, then displayName, then sku */
	get displayText (): string { return this.#displayText }

	/** Which fuel this item's catalog row is ('LP' / 'NG'), null when the appliance has no fuel variants */
	get fuelType (): string | null { return this.component?.fuelType ?? null }

	/** The pre-split base sku this item's catalog row belongs to, null when the appliance was never split into fuel variants */
	get fuelFamily (): string | null { return this.component?.fuelFamily ?? null }

	// A split catalog row already carries its fuel in the sku. A legacy row does not, and the suffix is
	// only appended when a real catalog row actually carries the suffixed sku — a fabricated one would
	// put a sku on a customer quote that exists in neither the catalog nor HubSpot.
	get fqSku (): string {
		if (!this.component) return '';
		if (this.component.fuelFamily || !this.component.fuel || !this.component.showFuelSku) return this.component.sku;

		const suffixed = `${this.component.sku}${getFuelSkuSuffix(this.refMap.kitchen?.fuelchoice)}`;

		return this.context?.components?.findSku(suffixed) ? suffixed : this.component.sku;
	}

	@Publish('item_size', 'updated')
	set size (size: Partial<Size>) {
		if (!this.#override) return;
		this.component.size = { ...this.component.size, ...size };
	}
	get size (): Size { return this.component?.size ?? null }

	@Publish('item_override', 'updated')
	set override (value: boolean) {
		this.component.readonly = !value;
		this.#override = value;
	}
	get override (): boolean { return this.#override }

	@Publish('item_sku_color', 'updated')
	set skuColor (color: string | null) {
		this.#skuColor = color;
	}
	get skuColor (): string | null { return this.#skuColor }

	// calculate these:

	/**
	 * destructor, responsible for cleanup
	*/
	public cleanup (): void {
		super.cleanup();
	}

	// serialize
	public serialize (): ItemDataOut {
		// don't serialize modelOffset
		const componentData = this.component?.serialize();
		if (componentData) delete componentData.modelOffset;

		return {
			id: this._id,
			override: this.#override,
			component: this.#override ? componentData : null,
			exclude: this.exclude,
			fuelchoice: this.fuelchoice,
			skuColor: this.#skuColor
		};
	}
}
